import React, { useEffect, useReducer, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import Popover from "@material-ui/core/Popover";

import Notifications from "@material-ui/icons/Notifications"

import {
  Avatar,
  Badge,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  Paper,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
} from "@material-ui/core";
import api from "../../services/api";
import { isArray } from "lodash";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTimezone } from "../../hooks/useTimezone";
import { i18n } from "../../translate/i18n";
import { markAnnouncementReadRemote } from "../../helpers/announcementAckStorage";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    maxHeight: 3000,
    maxWidth: 5000,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

function AnnouncementDialog({ announcement, open, handleClose }) {
  const getMediaPath = (filename) => {
    if (!filename) return null;
    // ✅ Si ya es una URL completa, retornarla tal cual
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }
    // ✅ Construir URL completa desde el backend
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${backendUrl}/public/${filename}`;
  };

  // ✅ Detectar tipo de archivo por extensión
  const getFileType = (filename) => {
    if (!filename) return 'unknown';
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
      return 'image';
    }
    if (['mp4', 'avi', 'mov', 'mkv', '3gp', 'webm'].includes(ext)) {
      return 'video';
    }
    if (['mp3', 'ogg', 'wav', 'm4a', 'aac'].includes(ext)) {
      return 'audio';
    }
    return 'document';
  };

  const mediaPath = getMediaPath(announcement.mediaPath);
  const fileType = getFileType(announcement.mediaPath);

  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="alert-dialog-title">{announcement.title}</DialogTitle>
      <DialogContent>
        {announcement.mediaPath && mediaPath && (
          <div style={{ marginBottom: 20, textAlign: 'center' }}>
            {fileType === 'image' && (
              <img
                src={mediaPath}
                alt={announcement.mediaName || announcement.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain',
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                }}
                onError={(e) => {
                  // ✅ Si la imagen falla al cargar, mostrar mensaje
                  e.target.style.display = 'none';
                  const errorDiv = document.createElement('div');
                  errorDiv.textContent = 'No se pudo cargar la imagen';
                  errorDiv.style.cssText = 'padding: 20px; color: #666; text-align: center;';
                  e.target.parentNode.appendChild(errorDiv);
                }}
              />
            )}
            {fileType === 'video' && (
              <video
                src={mediaPath}
                controls
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const errorDiv = document.createElement('div');
                  errorDiv.textContent = 'No se pudo cargar el video';
                  errorDiv.style.cssText = 'padding: 20px; color: #666; text-align: center;';
                  e.target.parentNode.appendChild(errorDiv);
                }}
              >
                Tu navegador no soporta la reproducción de video.
              </video>
            )}
            {fileType === 'audio' && (
              <div style={{ padding: 20 }}>
                <audio
                  src={mediaPath}
                  controls
                  style={{ width: '100%' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const errorDiv = document.createElement('div');
                    errorDiv.textContent = 'No se pudo cargar el audio';
                    errorDiv.style.cssText = 'padding: 20px; color: #666; text-align: center;';
                    e.target.parentNode.appendChild(errorDiv);
                  }}
                >
                  Tu navegador no soporta la reproducción de audio.
                </audio>
              </div>
            )}
            {(fileType === 'document' || fileType === 'unknown') && (
              <div style={{ 
                padding: 20, 
                border: '1px solid #e0e0e0', 
                borderRadius: 8,
                textAlign: 'center'
              }}>
                <Typography variant="body1" style={{ marginBottom: 10 }}>
                  📄 {announcement.mediaName || 'Archivo adjunto'}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  href={mediaPath}
                  download={announcement.mediaName}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Descargar archivo
                </Button>
              </div>
            )}
          </div>
        )}
        <DialogContentText id="alert-dialog-description">
          {announcement.text}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()} color="primary" autoFocus>
          {i18n.t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (u) => u.id === announcement.id
        );
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

export default function AnnouncementsPopover() {
  const classes = useStyles();
  const { formatDate } = useTimezone();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);
  const [invisible, setInvisible] = useState(false);
  const [announcement, setAnnouncement] = useState({});
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber, user?.id]);

  useEffect(() => {
    const companyKey =
      user?.companyId != null && user.companyId !== ""
        ? String(user.companyId)
        : localStorage.getItem("companyId");
    if (!companyKey) {
      return () => {};
    }
    const socket = socketManager.getSocket(companyKey);

    if (!socket) {
      return () => {};
    }

    const onCompanyAnnouncement = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({
          type: "UPDATE_ANNOUNCEMENTS",
          payload: { ...data.record, pendingRead: true }
        });
        setInvisible(false);
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
      }
    };

    socket.on(`company-announcement`, onCompanyAnnouncement);
    return () => {
      socket.off(`company-announcement`, onCompanyAnnouncement);
    };
  }, [socketManager, user?.companyId]);

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setInvisible(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const borderPriority = (priority) => {
    if (priority === 1) {
      return "4px solid #b81111";
    }
    if (priority === 2) {
      return "4px solid orange";
    }
    if (priority === 3) {
      return "4px solid grey";
    }
  };

  const borderForItem = (item) => {
    if (item.pendingRead === false) {
      return "4px solid #9e9e9e";
    }
    return borderPriority(item.priority);
  };

  const getMediaPath = (filename) => {
    return `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/public/${filename}`;
  };

  const handleShowAnnouncementDialog = (record) => {
    setAnnouncement(record);
    setShowAnnouncementDialog(true);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const hasPending = announcements.some((a) => a.pendingRead === true);

  return (
    <div>
      <AnnouncementDialog
        announcement={announcement}
        open={showAnnouncementDialog}
        handleClose={async () => {
          if (user?.id != null && announcement && announcement.id != null) {
            try {
              await markAnnouncementReadRemote(announcement.id);
              dispatch({
                type: "UPDATE_ANNOUNCEMENTS",
                payload: { ...announcement, pendingRead: false }
              });
            } catch (err) {
              toastError(err);
            }
          }
          setShowAnnouncementDialog(false);
        }}
      />
      <IconButton
        variant="contained"
        aria-describedby={id}
        onClick={handleClick}
        style={{ color: "white" }}
      >
        <Badge
          color="secondary"
          variant="dot"
          invisible={invisible || !hasPending}
        >
          <Notifications />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl || null}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Paper
          variant="outlined"
          onScroll={handleScroll}
          className={classes.mainPaper}
        >
          <List
            component="nav"
            aria-label="main mailbox folders"
            style={{ minWidth: 300 }}
          >
            {isArray(announcements) &&
              announcements.map((item, key) => (
                <ListItem
                  key={key}
                  style={{
                    //background: key % 2 === 0 ? "#ededed" : "white",
                    border: "1px solid #eee",
                    borderLeft: borderForItem(item),
                    cursor: "pointer",
                  }}
                  onClick={() => handleShowAnnouncementDialog(item)}
                >
                  {item.mediaPath && (
                    <ListItemAvatar>
                      <Avatar
                        alt={item.mediaName}
                        src={getMediaPath(item.mediaPath)}
                      />
                    </ListItemAvatar>
                  )}
                  <ListItemText
                    primary={item.title}
                    secondary={
                      <>
                        <Typography component="span" style={{ fontSize: 12 }}>
                          {formatDate(item.createdAt)}
                        </Typography>
                        <span style={{ marginTop: 5, display: "block" }}></span>
                        <Typography component="span" variant="body2">
                          {item.text}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            {isArray(announcements) && announcements.length === 0 && (
              <ListItemText primary={i18n.t("notifications.noRecords")} />
            )}
          </List>
        </Paper>
      </Popover>
    </div>
  );
}
